<template>

  <!-- Mobile view -->
  <template v-if="mobile">
    <!-- Fake card background -->
    <div aria-hidden="true" class="pointer-events-none absolute inset-0 hidden sm:block">
      <div class="shadow absolute right-0 w-1/2 h-full bg-white rounded-lg" />
    </div>

    <div class="ring-1 ring-black ring-opacity-5 shadow relative px-4 bg-white rounded-lg sm:p-0 sm:bg-transparent sm:rounded-none sm:ring-0 sm:shadow-none">
      <dl class="divide-y divide-gray-200">
        <div v-for="key of keys" :key="key" class="flex items-center justify-between sm:grid sm:grid-cols-2">
          <dt class="text-sm font-medium text-gray-600 flex items-center gap-2 justify-between w-full pr-8">
            <span class="uppercase">{{ $t(`pricing.${key}`) }}</span>
            <div class="flex items-center gap-1">
              <a v-if="$te(`pricing.${key}_help_link`)" :href="$t(`pricing.${key}_help_link`).toString()" target="_blank" :title="$t('pricing.additional_info').toString()">
                  <img src="~/assets/img/link-dark.svg" class="w-6 min-w-6">
                </a>
              <Tooltip class="text-right">{{ $t(`pricing.${key}_help`) }}</Tooltip>
            </div>
          </dt>
          <dd class="flex items-center justify-end sm:justify-center sm:px-4 w-full">
            <template v-if="typeof selectedPlanPricingData[key] === 'number'">
              {{ selectedPlanPricingData[key]}}
            </template>
            <template v-else-if="typeof selectedPlanPricingData[key] === 'boolean'">
              <CheckMark v-if="selectedPlanPricingData[key]" :class="{'text-highlight': false}" class="w-4 mx-auto fill-current" />
              <Xmark v-else class="w-3 mx-auto text-gray-300 fill-current" />
            </template>
            <template v-else-if="typeof selectedPlanPricingData[key] === 'string'">
              <p>{{ $t(selectedPlanPricingData[key]) }}</p>
            </template>
            <template v-else-if="typeof selectedPlanPricingData[key] === 'object'">
              <template v-if="(selectedPlanPricingData[key] as PricingComplexEntry).paymentFrequency && typeof (selectedPlanPricingData[key] as PricingComplexEntry).value === 'string'">
                <p :class="{'line-through': paymentFrequency !== selectedPlanPricingData[key].paymentFrequency}">{{ $t(selectedPlanPricingData[key].value) }}*</p>
              </template>
            </template>
          </dd>
        </div>

       
      </dl>
    </div>

    <!-- Fake card border -->
    <div aria-hidden="true" class="pointer-events-none absolute inset-0 hidden sm:block">
      <div class="ring-1 ring-black ring-opacity-5 absolute right-0 w-1/2 h-full rounded-lg" />
    </div>
  </template>

  <!-- Desktop view -->
  <template v-else>
    <!-- Fake card backgrounds -->
    <div class="pointer-events-none absolute inset-0 flex items-stretch" aria-hidden="true">
      <div class="w-1/5 pr-4" />
      <div v-for="price of pricingData" :key="price.key" class="w-1/5 px-4">
        <div :class="{'shadow': true, 'shadow-md': false}" class="h-full w-full rounded-lg bg-white shadow" />
      </div>
    </div>

    <table class="relative w-full">
      <caption class="sr-only">
        Plan feature comparison
      </caption>
      <thead>
        <tr class="text-left">
          <th scope="col">
            <span class="sr-only">Feature</span>
          </th>

          <th scope="col">
            <span class="sr-only">Starter plan</span>
          </th>

          <th scope="col">
            <span class="sr-only">Basic plan</span>
          </th>

          <th scope="col">
            <span class="sr-only">Pro plan</span>
          </th>

          <th scope="col">
            <span class="sr-only">Enterprise plan</span>
          </th>
        </tr>
      </thead>
      <tbody class="divide-y divide-gray-100">
        <tr v-for="key of keys" :key="key">
          <th scope="row" class="w-1/5 py-3 pr-4 text-left text-sm font-medium text-gray-600">
            <div class=" flex items-center gap-2 justify-between">
              <span class="uppercase">{{ $t(`pricing.${key}`) }}</span>
              <div class="flex items-center gap-1">
                <a v-if="$te(`pricing.${key}_help_link`)" :href="$t(`pricing.${key}_help_link`).toString()" target="_blank" :title="$t('pricing.additional_info').toString()">
                  <img src="~/assets/img/link-dark.svg" class="w-6 min-w-6">
                </a>
                <Tooltip class="w-12 text-right">{{ $t(`pricing.${key}_help`) }}</Tooltip>
              </div>
            </div>
          </th>
          <td v-for="price of pricingData" :key="price.key as string" class="px-4 relative w-1/5 py-0 text-center">
            <template v-if="typeof price[key] === 'number'">
              {{ price[key] }}
            </template>
            <template v-else-if="typeof price[key] === 'boolean'">
              <CheckMark v-if="price[key]" :class="{'text-highlight': false}" class="w-4 mx-auto fill-current" />
              <Xmark v-else class="w-3 mx-auto text-gray-300 fill-current" />
            </template>
            <template v-else-if="typeof price[key] === 'string'">
              <p>{{ $t(price[key]) }}</p>
            </template>
            <template v-else-if="typeof price[key] === 'object'">
              <template v-if="(price[key] as PricingComplexEntry).paymentFrequency && typeof (price[key] as PricingComplexEntry).value === 'string'">
                <p :class="{'line-through': paymentFrequency === 'monthly'}">{{ $t(price[key].value) }}*</p>
              </template>
            </template>
          </td>
        </tr>
      </tbody>
    </table>

    <!-- Fake card borders -->
    <div class="pointer-events-none absolute inset-0 flex items-stretch" aria-hidden="true">
      <div class="w-1/5 pr-4" />
      <div class="w-1/5 px-4">
        <div class="h-full w-full rounded-lg ring-1 ring-black ring-opacity-5" />
      </div>
      <div class="w-1/5 px-4">
        <div class="h-full w-full rounded-lg ring-2 ring-black ring-opacity-5" />
      </div>
      <div class="w-1/5 px-4">
        <div class="h-full w-full rounded-lg ring-1 ring-black ring-opacity-5" />
      </div>
      <div class="w-1/5 px-4">
        <div class="h-full w-full rounded-lg ring-1 ring-black ring-opacity-5" />
      </div>
    </div>
  </template>
</template>

<script lang="ts" setup>
import CheckMark from '@/components/CheckMark.vue'
import Xmark from '~/components/XMark.vue'

import { pricingData, type PricingComplexEntry } from "~/components/PricingBlock.vue"

  // Props
  const props = withDefaults(defineProps<{
    keys: string[],
    paymentFrequency: string,
    mobile?: boolean,
    selectedPlan?: string,
  }>(), {
    keys: [],
    paymentFrequency: 'monthly',
    mobile: false,
    selectedPlan: 'professional',
  })

  const selectedPlanPricingData = computed(() => {
    return pricingData.find(plan => plan.key === props.selectedPlan) || {}
  })
</script>